import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';
import io from 'socket.io-client';
import config from '../config';
import ShapeToolbar, { SHAPE_TYPES, COLORS } from './ShapeToolbar';
import ShapeRenderer from './ShapeRenderer';
import HighlightToolbar from './HighlightToolbar';
import './PDFViewer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// PDF.js worker is configured in index.js
// No need to set it here again

// Define PDF options OUTSIDE component to prevent recreation on every render
// This is critical - recreating options breaks the worker connection
const pdfOptions = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
  useWorkerFetch: false,
  isEvalSupported: false,
  verbosity: 0,
  disableAutoFetch: false, // Allow automatic fetching for faster loading
  disableStream: false, // Enable streaming for faster initial display
  disableRange: false, // Enable range requests for faster loading
  // Optimize loading performance
  stopAtErrors: false,
  maxImageSize: 1024 * 1024, // Limit image size to 1MB for faster processing
};

const PDFViewer = ({ pdfUrl, pdfId, shareId, userName, activeReviewId, comments, onCommentsChange, onCommentDelete, onCommentUpdate, onCommentReply, onCommentMarkRead, exportRef, sharePanel, selectedCommentId, onSelectComment, onDocumentReady }) => {
  // Debug: Log comments prop changes
  useEffect(() => {
    console.log('📄 PDFViewer comments prop:', {
      count: comments.length,
      comments: comments.map(c => ({ id: c.id, text: c.text?.substring(0, 30), page: c.page, author: c.author }))
    });
  }, [comments]);

  // Ensure worker is configured when component mounts
  useEffect(() => {
    // Verify worker is configured
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      const workerVersion = pdfjs.version || '3.11.174';
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${workerVersion}/build/pdf.worker.min.js`;
    }
  }, []);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  // Zoom is temporarily disabled - using fixed scale
  const scale = 1.0;
  const documentReady = !!numPages && numPages > 0;
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [tempComment, setTempComment] = useState(null);
  const [socket, setSocket] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [userCursors, setUserCursors] = useState({});
  const [shapes, setShapes] = useState([]);
  const [selectedShapeType, setSelectedShapeType] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedColor, setSelectedColor] = useState(COLORS[0].value);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingShape, setDrawingShape] = useState(null);
  const [isHighlightMode, setIsHighlightMode] = useState(false);
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('#FCE38A');
  const [highlights, setHighlights] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const mouseMoveTimeout = useRef(null);
  const drawingStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const annotationsExtractedRef = useRef(false);
  const socketRef = useRef(null);
  const [maxRenderedPage, setMaxRenderedPage] = useState(1);
  const [isFirstPageRendered, setIsFirstPageRendered] = useState(false);

  // Close users dropdown when clicking outside
  useEffect(() => {
    if (!showUsersDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.active-users-container')) {
        setShowUsersDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUsersDropdown]);

  // Keyboard shortcuts for zoom (temporarily disabled)
  // useEffect(() => {
  //   if (!documentReady) return;
  //   const handleKeyDown = (e) => {
  //     if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
  //       if (e.key === '=' || e.key === '+') {
  //         e.preventDefault();
  //         setScale(prev => Math.min(3.0, prev + 0.1));
  //       } else if (e.key === '-') {
  //         e.preventDefault();
  //         setScale(prev => Math.max(0.5, prev - 0.1));
  //       } else if (e.key === '0') {
  //         e.preventDefault();
  //         setScale(1.0);
  //       }
  //     }
  //   };
  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => document.removeEventListener('keydown', handleKeyDown);
  // }, [documentReady]);

  useEffect(() => {
    if (!pdfId) return;

    // Connect to socket
    const newSocket = io(config.SOCKET_URL, {
      forceNew: true, // Force new connection to avoid reusing old connections
      transports: ['websocket', 'polling']
    });
    setSocket(newSocket);
    socketRef.current = newSocket;

    // Join with user name and (optionally) a review context
    newSocket.emit('join-pdf', {
      pdfId: pdfId,
      userName: userName || `User ${Math.floor(Math.random() * 1000)}`,
      reviewId: activeReviewId || null,
    });

    // HTTP fallback: load comments from API so they show even if socket pdf-state is missed (e.g. Mac/Safari)
    const ac = new AbortController();
    const applySessionComments = (data) => {
      let serverComments = (data.comments || []).filter((c) => c && (c.id != null && c.page != null));
      // When viewing inside a specific review, only keep comments for that review.
      if (activeReviewId) {
        const reviewIdStr = String(activeReviewId);
        serverComments = serverComments.filter((c) => c.reviewId && String(c.reviewId) === reviewIdStr);
      }
      if (serverComments.length === 0) return false;
      onCommentsChange((prev) => {
        const serverIds = new Set(serverComments.map((c) => c.id));
        const localOnly = prev.filter((c) => !serverIds.has(c.id));
        const merged = [...serverComments, ...localOnly];
        const seenIds = new Set();
        return merged.map((c) => ({
          ...c,
          replies: Array.isArray(c.replies) ? c.replies.filter((r) => r && typeof r === 'object' && (r.text != null || r.author != null)) : []
        })).filter((c) => {
          if (seenIds.has(c.id)) return false;
          seenIds.add(c.id);
          return true;
        });
      });
      return true;
    };
    const fetchSession = (isRetry) => {
      fetch(`${config.API_URL}/api/pdf/${pdfId}`, { signal: ac.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Session not found'))))
        .then((data) => {
          if (applySessionComments(data)) return;
          if (!isRetry) {
            setTimeout(() => {
              if (ac.signal.aborted) return;
              fetch(`${config.API_URL}/api/pdf/${pdfId}`)
                .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Session not found'))))
                .then((retryData) => applySessionComments(retryData))
                .catch(() => {});
            }, 450);
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') console.warn('Session comments fallback fetch failed:', err.message);
        });
    };
    fetchSession(false);

    newSocket.on('pdf-state', (data) => {
      console.log('📥 Received pdf-state from server:', {
        commentsCount: (data.comments || []).length,
        shapesCount: (data.shapes || []).length,
        highlightsCount: (data.highlights || []).length,
        usersCount: (data.users || []).length,
        comments: data.comments
      });
      // Merge server comments with existing comments to avoid overwriting extracted PDF comments
      // Server comments are the source of truth, but we merge to preserve any local extractions
      let serverComments = data.comments || [];
      // When we are in a specific review context, only show comments for that review.
      if (activeReviewId) {
        const reviewIdStr = String(activeReviewId);
        serverComments = serverComments.filter((c) => c.reviewId && String(c.reviewId) === reviewIdStr);
      }
      if (serverComments.length > 0) {
        console.log('📝 Server comments:', serverComments.map(c => ({ id: c.id, text: c.text?.substring(0, 30), page: c.page })));
      } else {
        console.log('⚠️ Server sent empty comments array');
      }
      
      // Merge server comments with existing comments (avoid duplicates)
      onCommentsChange(prev => {
        const serverIds = new Set(serverComments.map(c => c.id));
        
        // Keep existing comments that aren't in server list (e.g., PDF-extracted comments not yet synced)
        const localOnly = prev.filter(c => !serverIds.has(c.id));
        
        // Combine: server comments (source of truth) + local-only comments
        const merged = [...serverComments, ...localOnly];
        
        // Remove duplicates by ID (server comments take precedence) and normalize replies
        const uniqueComments = [];
        const seenIds = new Set();
        for (const comment of merged) {
          if (!seenIds.has(comment.id)) {
            seenIds.add(comment.id);
            // Ensure every comment has a replies array
            const normalizedComment = {
              ...comment,
              replies: Array.isArray(comment.replies) ? comment.replies.filter((r) => r && typeof r === 'object' && (r.text != null || r.author != null)) : []
            };
            uniqueComments.push(normalizedComment);
          }
        }
        
        const totalReplies = uniqueComments.reduce((sum, c) => sum + (c.replies?.length || 0), 0);
        console.log(`📊 Merged comments: ${prev.length} existing + ${serverComments.length} server = ${uniqueComments.length} total (${totalReplies} replies)`);
        return uniqueComments;
      });
      
      setShapes(data.shapes || []);
      setHighlights(data.highlights || []);
      // Server already excludes current user, so use as-is
      setActiveUsers(data.users || []);
    });

    newSocket.on('user-joined', (user) => {
      setActiveUsers(prev => {
        // Don't add if it's ourselves or already exists
        if (user.id === newSocket.id) return prev;
        const exists = prev.some(u => u.id === user.id);
        return exists ? prev : [...prev, user];
      });
    });

    newSocket.on('user-left', (userId) => {
      setActiveUsers(prev => prev.filter(u => u.id !== userId));
      setUserCursors(prev => {
        const newCursors = { ...prev };
        delete newCursors[userId];
        return newCursors;
      });
    });

    newSocket.on('users-updated', (users) => {
      // Server sends users list excluding current user, so use directly
      // Ensure no duplicates and filter out any invalid entries
      setActiveUsers(() => {
        // Use Map to ensure uniqueness by user ID
        const userMap = new Map();
        (users || []).forEach(u => {
          if (u && u.id && u.id !== newSocket.id) {
            userMap.set(u.id, u);
          }
        });
        return Array.from(userMap.values());
      });
    });

    newSocket.on('user-position-updated', (data) => {
      setUserCursors(prev => ({
        ...prev,
        [data.userId]: {
          userName: data.userName,
          color: data.color,
          page: data.page,
          position: data.position
        }
      }));
    });

    newSocket.on('comment-added', (comment) => {
      console.log('📥 Received comment-added event:', { id: comment.id, text: comment.text?.substring(0, 30), page: comment.page });
      onCommentsChange(prev => {
        // Check if comment already exists to prevent duplicates
        const exists = prev.some(c => c.id === comment.id);
        if (exists) {
          console.log('⚠️ Comment already exists, skipping:', comment.id);
          return prev;
        }
        console.log('✅ Adding new comment from server:', comment.id);
        return [...prev, comment];
      });
    });

    newSocket.on('comment-updated', ({ commentId, updates }) => {
      onCommentsChange(prev =>
        prev.map(c => c.id === commentId ? { ...c, ...updates } : c)
      );
    });

    newSocket.on('comment-deleted', (commentId) => {
      onCommentsChange(prev => prev.filter(c => c.id !== commentId));
    });

    // Store socket reference for comment operations
    setSocket(newSocket);

    newSocket.on('shape-added', (shape) => {
      setShapes(prev => {
        const exists = prev.some(s => s.id === shape.id);
        return exists ? prev : [...prev, shape];
      });
    });

    newSocket.on('shape-deleted', (shapeId) => {
      setShapes(prev => prev.filter(s => s.id !== shapeId));
      if (selectedShapeId === shapeId) {
        setSelectedShapeId(null);
      }
    });

    newSocket.on('shape-updated', ({ shapeId, updates }) => {
      setShapes(prev =>
        prev.map(s => s.id === shapeId ? { ...s, ...updates } : s)
      );
    });

    newSocket.on('highlight-added', (highlight) => {
      setHighlights(prev => {
        const exists = prev.some(h => h.id === highlight.id);
        return exists ? prev : [...prev, highlight];
      });
    });

    newSocket.on('highlight-deleted', (highlightId) => {
      setHighlights(prev => prev.filter(h => h.id !== highlightId));
    });

    return () => {
      ac.abort();
      console.log('Cleaning up socket connection:', newSocket.id);
      newSocket.removeAllListeners();
      newSocket.disconnect();
      newSocket.close();
      socketRef.current = null;
      setActiveUsers([]);
      setUserCursors({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfId, userName, activeReviewId]);

  // Extract annotations from PDF when it loads (non-blocking, error-safe)
  // This runs in the background and doesn't interfere with PDF display
  const extractPDFAnnotations = async (pdfUrl) => {
    // Prevent multiple extractions
    // Note: We allow extraction even without socket - comments will be added to state
    if (annotationsExtractedRef.current || !pdfUrl || !pdfId) {
      console.log('Skipping extraction:', {
        alreadyExtracted: annotationsExtractedRef.current,
        hasPdfUrl: !!pdfUrl,
        hasPdfId: !!pdfId
      });
      return;
    }
    
    // Mark as extracting to prevent duplicate calls
    annotationsExtractedRef.current = true;
    console.log('Starting PDF annotation extraction for:', pdfUrl);
    
    // Wait for socket to be ready (with timeout)
    const waitForSocket = () => {
      return new Promise((resolve) => {
        // Check socket ref first (most up-to-date)
        const currentSocket = socketRef.current || socket;
        if (currentSocket && currentSocket.connected) {
          resolve(currentSocket);
          return;
        }
        // Wait up to 5 seconds for socket
        let attempts = 0;
        const checkSocket = setInterval(() => {
          attempts++;
          const checkSocketRef = socketRef.current || socket;
          if (checkSocketRef && checkSocketRef.connected) {
            clearInterval(checkSocket);
            resolve(checkSocketRef);
          } else if (attempts >= 50) { // 5 seconds max
            clearInterval(checkSocket);
            resolve(null); // Continue without socket
          }
        }, 100);
      });
    };
    
    // Run extraction asynchronously in the background - don't block anything
    const runExtraction = async () => {
      try {
        // Wait for socket to be ready (but don't block if it takes too long)
        const currentSocket = await waitForSocket();
        console.log('Socket status:', currentSocket ? 'ready' : 'not available, will add to state only');
        
        console.log('Loading PDF document for annotation extraction...');
        // Create a completely separate document loading task
        // This won't interfere with react-pdf's Document component
        const loadingTask = pdfjs.getDocument({
          url: pdfUrl,
          withCredentials: false,
          disableAutoFetch: false,
          disableStream: false
        });
        
        const pdf = await loadingTask.promise;
        console.log(`PDF loaded for extraction, pages: ${pdf.numPages}`);
        const extractedComments = [];
        
        // Map to track popup annotations and their parent annotations
        const popupMap = new Map();
        const annotationMap = new Map();
        
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          try {
            console.log(`Extracting annotations from page ${pageNum}...`);
            const page = await pdf.getPage(pageNum);
            
            // Try getting annotations with different methods
            let annotations = [];
            try {
              // Try standard method first
              annotations = await page.getAnnotations();
              console.log(`Page ${pageNum}: Found ${annotations.length} annotations (standard method)`);
            } catch (e1) {
              try {
                // Try with display intent
                annotations = await page.getAnnotations({ intent: 'display' });
                console.log(`Page ${pageNum}: Found ${annotations.length} annotations (with intent: display)`);
              } catch (e2) {
                try {
                  // Try with print intent
                  annotations = await page.getAnnotations({ intent: 'print' });
                  console.log(`Page ${pageNum}: Found ${annotations.length} annotations (with intent: print)`);
                } catch (e3) {
                  try {
                    // Try accessing annotations directly from the page object
                    if (page.annotations) {
                      annotations = page.annotations;
                      console.log(`Page ${pageNum}: Found ${annotations.length} annotations (direct access)`);
                    } else {
                      console.warn(`Page ${pageNum}: No annotations found via any method`);
                      annotations = [];
                    }
                  } catch (e4) {
                    console.error(`Error getting annotations from page ${pageNum}:`, e4);
                    annotations = [];
                  }
                }
              }
            }
            
            // Log annotation details for debugging
            if (annotations.length > 0) {
              console.log(`Page ${pageNum} annotation details:`, annotations.map(ann => ({
                subtype: ann.subtype,
                type: ann.annotationType,
                hasContents: !!ann.contents,
                hasTitle: !!ann.title,
                id: ann.id,
                objId: ann.objId
              })));
            }
            
            const viewport = page.getViewport({ scale: 1.0 });
            
            if (annotations.length === 0) {
              console.log(`Page ${pageNum}: No annotations found`);
            }
            
            // First pass: collect all annotations and map popups to their parents
            for (const annotation of annotations) {
              const annotationId = annotation.id || annotation.objId || annotation.ref || `${pageNum}-${Date.now()}-${Math.random()}`;
              annotationMap.set(annotationId, { annotation, pageNum, viewport });
              
              // Check if this is a popup annotation
              if (annotation.subtype === 'Popup' || annotation.subtype === 'popup' || annotation.annotationType === 'Popup') {
                // Find parent annotation - popups can be linked via various properties
                const parentId = annotation.parentId || annotation.parentObjId || annotation.parent || annotation.parentRef;
                const parentObjId = annotation.parentObjId || annotation.parent;
                
                // Try to find parent by objId or ref
                if (parentId || parentObjId) {
                  // Store popup with parent reference
                  for (const [id, { annotation: ann }] of annotationMap.entries()) {
                    if (ann.id === parentId || ann.objId === parentId || ann.id === parentObjId || ann.objId === parentObjId) {
                      popupMap.set(id, annotation);
                      break;
                    }
                  }
                  // Also store by the parentId directly in case we haven't seen the parent yet
                  if (parentId) {
                    popupMap.set(parentId, annotation);
                  }
                }
              }
            }
            
            // Also do a reverse lookup - find annotations that reference this popup
            for (const annotation of annotations) {
              if (annotation.subtype !== 'Popup' && annotation.subtype !== 'popup') {
                const annotationId = annotation.id || annotation.objId || annotation.ref || `${pageNum}-${Date.now()}-${Math.random()}`;
                // Check if this annotation has a popup
                const popupRef = annotation.popupRef || annotation.popupId || annotation.popup;
                if (popupRef) {
                  // Find the popup annotation
                  for (const ann of annotations) {
                    if ((ann.id === popupRef || ann.objId === popupRef || ann.ref === popupRef) && 
                        (ann.subtype === 'Popup' || ann.subtype === 'popup')) {
                      popupMap.set(annotationId, ann);
                      break;
                    }
                  }
                }
              }
            }
            
            // Second pass: extract comments from annotations
            for (const [annotationId, { annotation, pageNum, viewport }] of annotationMap.entries()) {
              // Log all annotation properties for debugging
              console.log(`Annotation on page ${pageNum}:`, {
                subtype: annotation.subtype,
                type: annotation.annotationType,
                contents: annotation.contents,
                title: annotation.title,
                subject: annotation.subject,
                author: annotation.author,
                rect: annotation.rect,
                id: annotation.id,
                objId: annotation.objId,
                allKeys: Object.keys(annotation)
              });
              
              // Extract various types of annotations that contain comments
              // Text, FreeText, Highlight, Underline, StrikeOut, etc.
              const annotationTypes = ['Text', 'FreeText', 'Highlight', 'Underline', 'StrikeOut', 'Squiggly', 'Note', 'Popup', 'Widget', 'Ink', 'Stamp', 'Square', 'Circle', 'Polygon', 'PolyLine', 'Line', 'Caret'];
              const hasCommentText = annotation.contents && String(annotation.contents).trim();
              const hasTitle = annotation.title && String(annotation.title).trim();
              const hasSubject = annotation.subject && String(annotation.subject).trim();
              const annotationSubtype = (annotation.subtype || annotation.annotationType || '').toString();
              const isCommentType = annotationTypes.some(type => 
                annotationSubtype.toLowerCase() === type.toLowerCase()
              );

              // Special handling for popup-only annotations:
              // Some tools store the comment text ONLY in the Popup annotation without
              // linking it properly to a parent – handle those directly here.
              if (annotation.subtype === 'Popup' || annotation.subtype === 'popup') {
                const popupHasText = hasCommentText || hasTitle || hasSubject ||
                  (annotation.text && String(annotation.text).trim()) ||
                  (annotation.richText && String(annotation.richText).trim()) ||
                  (annotation.textContent && String(annotation.textContent).trim());

                if (popupHasText) {
                  const rect = annotation.rect || [0, 0, 0, 0];
                  const centerX = (rect[0] + rect[2]) / 2;
                  const centerY = (rect[1] + rect[3]) / 2;
                  const x = (centerX / viewport.width) * 100;
                  const y = 100 - ((centerY / viewport.height) * 100);

                  let commentText = '';
                  if (annotation.contents && String(annotation.contents).trim()) {
                    commentText = String(annotation.contents).trim();
                  } else if (annotation.richText && String(annotation.richText).trim()) {
                    commentText = String(annotation.richText).trim();
                  } else if (annotation.text && String(annotation.text).trim()) {
                    commentText = String(annotation.text).trim();
                  } else if (annotation.title && String(annotation.title).trim()) {
                    commentText = String(annotation.title).trim();
                  } else if (annotation.subject && String(annotation.subject).trim()) {
                    commentText = String(annotation.subject).trim();
                  }

                  let author = 'PDF Annotation';
                  if (annotation.author && String(annotation.author).trim()) {
                    author = String(annotation.author).trim();
                  } else if (annotation.name && String(annotation.name).trim()) {
                    author = String(annotation.name).trim();
                  } else if (annotation.T && String(annotation.T).trim()) {
                    author = String(annotation.T).trim();
                  }

                  // Parse replies from popup content if present
                  let replies = [];
                  const repliesSeparator = '\n\n--- Replies ---\n';
                  if (commentText.includes(repliesSeparator)) {
                    const parts = commentText.split(repliesSeparator);
                    commentText = parts[0] || '';
                    const repliesText = parts[1] || '';
                    
                    // Extract author from comment text if format is "Author: text"
                    const commentMatch = commentText.match(/^(.+?):\s*(.+)$/);
                    if (commentMatch) {
                      commentText = commentMatch[2].trim();
                      if (!author || author === 'PDF Annotation') {
                        author = commentMatch[1].trim();
                      }
                    }
                    
                    // Parse replies
                    if (repliesText.trim()) {
                      const replyLines = repliesText.split('\n').filter(line => line.trim());
                      for (const line of replyLines) {
                        const replyMatch = line.match(/^(.+?):\s*(.+)$/);
                        if (replyMatch) {
                          replies.push({
                            id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            author: replyMatch[1].trim(),
                            text: replyMatch[2].trim(),
                            timestamp: new Date().toISOString()
                          });
                        }
                      }
                    }
                  } else {
                    // Try to extract author if format is "Author: text"
                    const match = commentText.match(/^(.+?):\s*(.+)$/);
                    if (match) {
                      if (!author || author === 'PDF Annotation') {
                        author = match[1].trim();
                      }
                      commentText = match[2].trim();
                    }
                  }
                  
                  if (commentText.trim() || replies.length > 0) {
                    const comment = {
                      id: `pdf-annotation-${pageNum}-${annotation.id || annotation.objId || Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                      page: pageNum,
                      x: Math.max(0, Math.min(100, x)),
                      y: Math.max(0, Math.min(100, y)),
                      text: commentText.trim() || (replies.length > 0 ? '(No text)' : ''),
                      author: author,
                      timestamp: annotation.modificationDate ? 
                        new Date(annotation.modificationDate).toISOString() : 
                        (annotation.creationDate ? 
                          new Date(annotation.creationDate).toISOString() : 
                          new Date().toISOString()),
                      isFromPDF: true,
                      annotationType: annotation.subtype || 'Popup',
                      replies: replies.length > 0 ? replies : []
                    };
                    extractedComments.push(comment);
                    console.log(`Extracted popup-only comment from page ${pageNum}:`, comment.text.substring(0, 50), `(${replies.length} replies)`);
                  }
                }

                // After handling popup-only case, continue so we don't treat it again below
                continue;
              }
              
              // Check for associated popup annotation - try multiple keys
              let popupAnnotation = popupMap.get(annotationId);
              if (!popupAnnotation) {
                // Try finding by objId
                const objId = annotation.objId || annotation.id;
                if (objId) {
                  popupAnnotation = popupMap.get(objId);
                }
              }
              if (!popupAnnotation) {
                // Try finding by ref
                const ref = annotation.ref || annotation.id;
                if (ref) {
                  popupAnnotation = popupMap.get(ref);
                }
              }
              
              const popupContents = popupAnnotation && popupAnnotation.contents ? String(popupAnnotation.contents).trim() : '';
              
              // Also check for any text-like properties
              const hasAnyText = hasCommentText || hasTitle || hasSubject || popupContents ||
                (annotation.text && String(annotation.text).trim()) ||
                (annotation.richText && String(annotation.richText).trim()) ||
                (annotation.textContent && String(annotation.textContent).trim());
              
              // Extract if it has text content or is a comment type annotation
              // Be more permissive - extract any annotation with content
              if (isCommentType || hasAnyText) {
                const rect = annotation.rect || [0, 0, 0, 0];
                
                // Convert PDF coordinates to percentage
                const centerX = (rect[0] + rect[2]) / 2;
                const centerY = (rect[1] + rect[3]) / 2;
                const x = (centerX / viewport.width) * 100;
                const y = 100 - ((centerY / viewport.height) * 100);
                
                // Get full content from annotation (may include comment + replies)
                // Format: "Author: Comment text\n\n--- Replies ---\nAuthor1: Reply1\nAuthor2: Reply2"
                let fullContent = '';
                if (popupContents) {
                  fullContent = popupContents;
                } else if (annotation.contents && String(annotation.contents).trim()) {
                  fullContent = String(annotation.contents).trim();
                } else if (annotation.title && String(annotation.title).trim()) {
                  fullContent = String(annotation.title).trim();
                } else if (annotation.subject && String(annotation.subject).trim()) {
                  fullContent = String(annotation.subject).trim();
                } else if (annotation.richText && String(annotation.richText).trim()) {
                  fullContent = String(annotation.richText).trim();
                } else if (annotation.text && String(annotation.text).trim()) {
                  fullContent = String(annotation.text).trim();
                }
                
                // Parse comment and replies from fullContent
                let commentText = '';
                let replies = [];
                let extractedAuthor = null;
                
                if (fullContent.trim()) {
                  const repliesSeparator = '\n\n--- Replies ---\n';
                  const hasReplies = fullContent.includes(repliesSeparator);
                  
                  if (hasReplies) {
                    const parts = fullContent.split(repliesSeparator);
                    const commentPart = parts[0] || '';
                    const repliesText = parts[1] || '';
                    
                    // Extract author and text from comment part (format: "Author: text")
                    const commentMatch = commentPart.match(/^(.+?):\s*(.+)$/);
                    if (commentMatch) {
                      extractedAuthor = commentMatch[1].trim();
                      commentText = commentMatch[2].trim();
                    } else {
                      commentText = commentPart.trim();
                    }
                    
                    // Parse replies (format: "Author: text\nAuthor: text")
                    if (repliesText.trim()) {
                      const replyLines = repliesText.split('\n').filter(line => line.trim());
                      for (const line of replyLines) {
                        const replyMatch = line.match(/^(.+?):\s*(.+)$/);
                        if (replyMatch) {
                          replies.push({
                            id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            author: replyMatch[1].trim(),
                            text: replyMatch[2].trim(),
                            timestamp: new Date().toISOString()
                          });
                        }
                      }
                    }
                  } else {
                    // No replies separator, treat entire content as comment
                    // Try to extract author if format is "Author: text"
                    const match = fullContent.match(/^(.+?):\s*(.+)$/);
                    if (match) {
                      extractedAuthor = match[1].trim();
                      commentText = match[2].trim();
                    } else {
                      commentText = fullContent.trim();
                    }
                  }
                }
                
                // Get author from various sources (prefer extracted author from content)
                let author = 'PDF Annotation';
                if (extractedAuthor) {
                  author = extractedAuthor;
                } else if (annotation.author && String(annotation.author).trim()) {
                  author = String(annotation.author).trim();
                } else if (popupAnnotation && popupAnnotation.author && String(popupAnnotation.author).trim()) {
                  author = String(popupAnnotation.author).trim();
                } else if (annotation.title && String(annotation.title).trim() && String(annotation.title).trim() !== fullContent) {
                  author = String(annotation.title).trim();
                } else if (annotation.name && String(annotation.name).trim()) {
                  author = String(annotation.name).trim();
                } else if (annotation.T && String(annotation.T).trim()) {
                  author = String(annotation.T).trim();
                }
                
                // Only add if we have meaningful text or replies
                if (commentText.trim() || replies.length > 0) {
                  const comment = {
                    id: `pdf-annotation-${pageNum}-${annotation.id || annotation.objId || Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    page: pageNum,
                    x: Math.max(0, Math.min(100, x)),
                    y: Math.max(0, Math.min(100, y)),
                    text: commentText.trim() || (replies.length > 0 ? '(No text)' : ''),
                    author: author,
                    timestamp: annotation.modificationDate ? 
                      new Date(annotation.modificationDate).toISOString() : 
                      (annotation.creationDate ? 
                        new Date(annotation.creationDate).toISOString() : 
                        (popupAnnotation && popupAnnotation.modificationDate ?
                          new Date(popupAnnotation.modificationDate).toISOString() :
                          new Date().toISOString())),
                    isFromPDF: true,
                    annotationType: annotation.subtype || 'Unknown',
                    replies: replies.length > 0 ? replies : []
                  };
                  extractedComments.push(comment);
                  console.log(`Extracted comment from page ${pageNum}:`, comment.text.substring(0, 50), `(${replies.length} replies)`);
                }
              }
            }
          } catch (pageError) {
            console.error(`Error extracting annotations from page ${pageNum}:`, pageError);
            // Continue with other pages
            continue;
          }
        }
        
        // Clean up the loading task
        try {
          await loadingTask.destroy();
        } catch (destroyError) {
          console.warn('Error destroying PDF loading task:', destroyError);
        }
        
        // Add extracted comments to the comments list only if we have any
        if (extractedComments.length > 0 && pdfId) {
          console.log(`✅ Extracted ${extractedComments.length} comments from PDF annotations:`, 
            extractedComments.map(c => ({ id: c.id, text: c.text?.substring(0, 30), page: c.page, author: c.author })));
          
          // Immediately update the comments state - use functional update to ensure we get latest state
          onCommentsChange(prev => {
            // Merge with existing comments, avoiding duplicates
            const existingIds = new Set(prev.map(c => c.id));
            const newComments = extractedComments.filter(c => !existingIds.has(c.id));
            
            if (newComments.length > 0) {
              console.log(`✅ Adding ${newComments.length} new comments from PDF to sidebar`);
              console.log(`📊 Current comments: ${prev.length}, New comments: ${newComments.length}`);
              
              // Send to server via socket if available (but don't wait for it)
              if (currentSocket && currentSocket.connected) {
                console.log(`📤 Sending ${newComments.length} extracted comments to server...`);
                newComments.forEach(comment => {
                  try {
                    currentSocket.emit('add-comment', {
                      pdfId,
                      comment
                    });
                    console.log(`📤 Sent comment to server:`, { id: comment.id, text: comment.text?.substring(0, 30), page: comment.page });
                  } catch (err) {
                    console.error('Error sending comment to server:', err);
                  }
                });
              } else {
                console.log('⚠️ Socket not available, comments added to local state only');
                // Try to send later when socket is ready
                setTimeout(() => {
                  const laterSocket = socketRef.current || socket;
                  if (laterSocket && laterSocket.connected) {
                    console.log('📤 Retrying to send extracted comments to server...');
                    newComments.forEach(comment => {
                      try {
                        laterSocket.emit('add-comment', {
                          pdfId,
                          comment
                        });
                      } catch (err) {
                        console.error('Error sending comment to server on retry:', err);
                      }
                    });
                  }
                }, 2000);
              }
              
              const updatedComments = [...prev, ...newComments];
              console.log(`✅ Updated comments state: ${prev.length} → ${updatedComments.length} comments`);
              return updatedComments;
            } else {
              console.log('⚠️ All extracted comments already exist in state');
            }
            return prev;
          });
        } else {
          if (extractedComments.length === 0) {
            console.log('ℹ️ No comments found in PDF annotations');
          } else {
            console.log('⚠️ Extracted comments but no pdfId available');
          }
        }
      } catch (error) {
        console.error('❌ Error extracting PDF annotations:', error);
        // Reset the flag so it can be retried if PDF is reloaded
        annotationsExtractedRef.current = false;
      }
    };
    
    // Run extraction after PDF is fully loaded and displayed
    // Reduced delay to make extraction faster while still ensuring PDF is ready
    setTimeout(runExtraction, 1500);
  };

  const onDocumentLoadSuccess = async ({ numPages }) => {
    // Set numPages first to ensure PDF is displayed
    console.log('PDF loaded successfully, pages:', numPages);
    setNumPages(numPages);
    // Reset to page 1 when new PDF loads
    setPageNumber(1);
    // Start with the first few pages so large PDFs feel responsive
    setMaxRenderedPage(Math.min(numPages || 1, 5));
    setIsFirstPageRendered(false);
    onDocumentReady?.();
    
    // Extract PDF annotations (comments from PDF filler, etc.) after PDF loads
    // Extract even if socket isn't ready - comments will be added to state
    if (pdfUrl && pdfId && !annotationsExtractedRef.current && numPages && numPages > 0) {
      console.log('PDF loaded, scheduling annotation extraction...', {
        pdfUrl,
        pdfId,
        numPages,
        alreadyExtracted: annotationsExtractedRef.current
      });
      // Wait a bit for PDF to fully render, then extract annotations
      setTimeout(() => {
        if (pdfId && pdfUrl && !annotationsExtractedRef.current) {
          try {
            console.log('Triggering PDF annotation extraction...');
            extractPDFAnnotations(pdfUrl);
          } catch (err) {
            console.error('Error triggering PDF annotation extraction:', err);
            annotationsExtractedRef.current = false;
          }
        } else {
          console.log('Skipping extraction - conditions not met:', {
            hasPdfId: !!pdfId,
            hasPdfUrl: !!pdfUrl,
            alreadyExtracted: annotationsExtractedRef.current
          });
        }
      }, 1500);
    } else {
      console.log('Not extracting annotations:', {
        hasPdfUrl: !!pdfUrl,
        hasPdfId: !!pdfId,
        alreadyExtracted: annotationsExtractedRef.current,
        numPages
      });
    }
  };

  // Reset state when PDF URL changes
  useEffect(() => {
    annotationsExtractedRef.current = false;
    setNumPages(null);
    setPageNumber(1);
    setMaxRenderedPage(1);
    setIsFirstPageRendered(false);
  }, [pdfUrl]);

  // Gradually increase how many pages are rendered for large PDFs
  // Optimized: Load first 3 pages immediately, then progressively load remaining pages faster
  useEffect(() => {
    if (!numPages || maxRenderedPage >= numPages) return;
    
    // Load first 3 pages immediately for faster initial display
    if (maxRenderedPage === 1 && numPages > 1) {
      setMaxRenderedPage(Math.min(3, numPages));
      return;
    }
    
    const interval = setInterval(() => {
      setMaxRenderedPage(prev => {
        if (!numPages || prev >= numPages) {
          clearInterval(interval);
          return prev;
        }
        // Load 2 pages at a time for faster loading
        return Math.min(prev + 2, numPages);
      });
    }, 100); // Reduced from 250ms to 100ms for faster loading
    
    return () => clearInterval(interval);
  }, [numPages, maxRenderedPage]);

  // Track mouse movement for cursor sharing
  const handleMouseMove = (e) => {
    if (!socket || !pdfId || !numPages) return;

    // Find which page the mouse is currently over
    let currentPage = null;
    let pageElement = null;
    
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const pageEl = pageRefs.current[pageNum];
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        if (e.clientX >= rect.left && e.clientX <= rect.right &&
            e.clientY >= rect.top && e.clientY <= rect.bottom) {
          currentPage = pageNum;
          pageElement = pageEl;
          break;
        }
      }
    }

    if (!currentPage || !pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Update pageNumber for tracking
    if (currentPage !== pageNumber) {
      setPageNumber(currentPage);
    }

    // Throttle position updates
    if (mouseMoveTimeout.current) {
      clearTimeout(mouseMoveTimeout.current);
    }

    mouseMoveTimeout.current = setTimeout(() => {
      socket.emit('user-position', {
        pdfId,
        page: currentPage,
        position: { x, y }
      });
    }, 100);
  };

  // Track page changes (for initial position)
  useEffect(() => {
    if (socket && pdfId && numPages) {
      // Set initial page to 1
      setPageNumber(1);
      socket.emit('user-position', {
        pdfId,
        page: 1,
        position: { x: 0, y: 0 }
      });
    }
  }, [socket, pdfId, numPages]);

  // Close comment when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectedCommentId && 
          !e.target.closest('.comment-display-container') && 
          !e.target.closest('.comment-marker')) {
        onSelectComment?.(null);
      }
    };

    if (selectedCommentId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectedCommentId, onSelectComment]);

  const handlePageClick = (e, pageNum) => {
    // Close comment if clicking outside of comment elements
    if (selectedCommentId && !e.target.closest('.comment-display-container') && !e.target.closest('.comment-marker')) {
      onSelectComment?.(null);
    }

    if (!isAddingComment || isHighlightMode) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const comment = {
      id: uuidv4(),
      page: pageNum,
      x: x,
      y: y,
      text: '',
      author: userName || `User ${Math.floor(Math.random() * 1000)}`,
      timestamp: new Date().toISOString(),
      isNew: true
    };

    setTempComment(comment);
    setIsAddingComment(false);
  };

  const handleCommentSubmit = (commentText) => {
    if (!tempComment || !socket || !pdfId) return;

    const comment = {
      ...tempComment,
      text: commentText,
      isNew: false,
      // Ensure new comments carry the current review context (if any) so that
      // the server can store them as review-specific threads.
      reviewId: activeReviewId || null,
    };

    // Add comment locally first to show immediately
    onCommentsChange(prev => {
      // Check if comment already exists to prevent duplicates
      const exists = prev.some(c => c.id === comment.id);
      return exists ? prev : [...prev, comment];
    });

    // Then emit to server (server will broadcast back, but we'll prevent duplicates)
    socket.emit('add-comment', {
      pdfId,
      comment,
      reviewId: activeReviewId || null,
    });

    setTempComment(null);
  };

  const handleCommentCancel = () => {
    setTempComment(null);
    setIsAddingComment(false);
  };

  const getCommentsForPage = (pageNum) => {
    return comments.filter(c => c.page === pageNum);
  };

  const getShapesForPage = (pageNum) => {
    return shapes.filter(s => s.page === pageNum);
  };

  const getHighlightsForPage = (pageNum) => {
    return highlights.filter(h => h.page === pageNum);
  };

  // Handle text selection for highlighting
  useEffect(() => {
    if (!isHighlightMode) {
      // Clear selection when highlight mode is off
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
      return;
    }

    const handleMouseUp = (e) => {
      // Don't process if clicking on buttons or controls
      if (e.target.closest('button') || e.target.closest('.pdf-controls') || e.target.closest('.shape-toolbar') || e.target.closest('.highlight-toolbar')) {
        return;
      }

      // Don't process if in drawing or comment mode
      if (isDrawingMode || isAddingComment) {
        return;
      }

      // Small delay to ensure selection is complete
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const selectedText = range.toString().trim();
        
        if (!selectedText || selectedText.length === 0) return;

      // Find which page the selection is on
      let selectedPage = null;
      let pageElement = null;

      // Check if selection is within a PDF page
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const pageEl = pageRefs.current[pageNum];
        if (pageEl && pageEl.contains(range.commonAncestorContainer)) {
          selectedPage = pageNum;
          pageElement = pageEl;
          break;
        }
      }

      if (!selectedPage || !pageElement) {
        selection.removeAllRanges();
        return;
      }

      // Get page container bounds first (needed for coordinate conversion)
      const pageContainer = pageElement.querySelector('.page-container');
      if (!pageContainer) {
        selection.removeAllRanges();
        return;
      }
      const pageRect = pageContainer.getBoundingClientRect();

      // Estimate line height from selection context (for splitting single tall rect)
      let lineHeightPx = 18;
      try {
        const startNode = range.startContainer;
        const el = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode;
        if (el) {
          const style = window.getComputedStyle(el);
          const fs = parseFloat(style.fontSize) || 16;
          const lh = style.lineHeight;
          lineHeightPx = lh && lh !== 'normal' ? parseFloat(lh) : fs * 1.2;
        }
      } catch (_) { /* keep default */ }

      // Get bounding rectangles of selected text (one per line in most browsers)
      let rects = Array.from(range.getClientRects());
      if (rects.length === 0) {
        selection.removeAllRanges();
        return;
      }

      // Filter out zero-area or tiny rects first (e.g. Safari extra element-boundary rects)
      const minSize = 2;
      rects = rects.filter((r) => r.width >= minSize && r.height >= minSize);

      // Merge rects that are on the same line (same top within tolerance) so we get one rect per line
      const lineTolerance = 3;
      const byLine = new Map();
      for (const r of rects) {
        const topKey = Math.round(r.top / lineTolerance) * lineTolerance;
        if (!byLine.has(topKey)) {
          byLine.set(topKey, { left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width, height: r.height });
        } else {
          const m = byLine.get(topKey);
          m.left = Math.min(m.left, r.left);
          m.right = Math.max(m.right, r.right);
          m.top = Math.min(m.top, r.top);
          m.bottom = Math.max(m.bottom, r.bottom);
          m.width = m.right - m.left;
          m.height = m.bottom - m.top;
        }
      }
      rects = Array.from(byLine.values()).sort((a, b) => a.top - b.top || a.left - b.left);

      // When browser returns a single rect for multi-line selection, split into one rect per line (consistent line height)
      if (rects.length === 1 && rects[0].height > lineHeightPx * 1.2) {
        const singleRect = rects[0];
        const n = Math.max(1, Math.ceil(singleRect.height / lineHeightPx));
        const stripHeight = lineHeightPx;
        rects = [];
        for (let i = 0; i < n; i++) {
          const top = singleRect.top + i * stripHeight;
          const bottom = Math.min(top + stripHeight, singleRect.bottom);
          rects.push({
            left: singleRect.left,
            top,
            width: singleRect.width,
            height: bottom - top,
            right: singleRect.right,
            bottom
          });
        }
      }

      // One highlight per text segment (per line) so only the text is highlighted, not a box
      const newHighlights = [];
      for (let i = 0; i < rects.length; i++) {
        const rect = rects[i];
        const x = ((rect.left - pageRect.left) / pageRect.width) * 100;
        const y = ((rect.top - pageRect.top) / pageRect.height) * 100;
        const width = (rect.width / pageRect.width) * 100;
        const height = (rect.height / pageRect.height) * 100;
        newHighlights.push({
          id: uuidv4(),
          page: selectedPage,
          x,
          y,
          width,
          height,
          text: selectedText,
          color: selectedHighlightColor,
          author: userName,
          timestamp: new Date().toISOString()
        });
      }

      if (newHighlights.length > 0) {
        setHighlights(prev => [...prev, ...newHighlights]);
        if (socket && pdfId) {
          newHighlights.forEach(highlight => {
            socket.emit('add-highlight', { pdfId, highlight });
          });
        }
      }

      selection.removeAllRanges();
      }, 10); // Small delay to ensure selection is captured
    };

    document.addEventListener('mouseup', handleMouseUp, true);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [isHighlightMode, numPages, selectedHighlightColor, userName, socket, pdfId, isDrawingMode, isAddingComment]);

  const handleShapeTypeSelect = (shapeType) => {
    if (selectedShapeType === shapeType) {
      setSelectedShapeType(null);
    } else {
      setSelectedShapeType(shapeType);
      setIsAddingComment(false);
      setSelectedShapeId(null);
    }
  };

  const handleShapeStart = (e, pageNum) => {
    // Don't start drawing if clicking on an existing shape
    if (e.target.closest('.shape-item') || e.target.closest('.shape-selection-handle')) {
      return;
    }

    // Don't start drawing if in highlight mode
    if (isHighlightMode) return;

    if (!selectedShapeType || !isDrawingMode) return;

    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    drawingStartRef.current = { x, y, page: pageNum };
    setDrawingShape({
      id: uuidv4(),
      type: selectedShapeType,
      page: pageNum,
      x1: x,
      y1: y,
      x2: x,
      y2: y,
      color: selectedColor,
      author: userName
    });
  };

  const handleShapeMove = (e, pageNum) => {
    if (!isDraggingRef.current || !drawingShape || !drawingStartRef.current || drawingStartRef.current.page !== pageNum) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setDrawingShape(prev => ({
      ...prev,
      x2: x,
      y2: y
    }));
  };

  const handleShapeEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    if (!drawingShape || !socket || !pdfId) {
      setDrawingShape(null);
      return;
    }

    // Only save if shape has meaningful size
    const width = Math.abs(drawingShape.x2 - drawingShape.x1);
    const height = Math.abs(drawingShape.y2 - drawingShape.y1);
    
    if (width > 0.5 && height > 0.5) {
      const shape = {
        ...drawingShape,
        timestamp: new Date().toISOString()
      };

      // Add shape locally
      setShapes(prev => {
        const exists = prev.some(s => s.id === shape.id);
        return exists ? prev : [...prev, shape];
      });

      // Emit to server
      socket.emit('add-shape', {
        pdfId,
        shape
      });
    }

    setDrawingShape(null);
    drawingStartRef.current = null;
  };

  const handleShapeClick = (e, shapeId) => {
    e.stopPropagation();
    e.preventDefault();
    const shape = shapes.find((s) => s.id === shapeId);
    if (!shape) return;
    // In Add Comment mode, open comment input at shape center instead of selecting the shape
    if (isAddingComment) {
      const centerX = (shape.x1 + shape.x2) / 2;
      const centerY = (shape.y1 + shape.y2) / 2;
      setTempComment({
        id: uuidv4(),
        page: shape.page,
        x: centerX,
        y: centerY,
        text: '',
        author: userName || `User ${Math.floor(Math.random() * 1000)}`,
        timestamp: new Date().toISOString(),
        isNew: true
      });
      setIsAddingComment(false);
      return;
    }
    setSelectedShapeId(shapeId);
    setSelectedShapeType(null);
    setIsDrawingMode(false);
    setIsAddingComment(false);
  };

  const handleAddCommentToShape = () => {
    if (!selectedShapeId) return;
    const shape = shapes.find((s) => s.id === selectedShapeId);
    if (!shape) return;
    const centerX = (shape.x1 + shape.x2) / 2;
    const centerY = (shape.y1 + shape.y2) / 2;
    setPageNumber(shape.page);
    setTempComment({
      id: uuidv4(),
      page: shape.page,
      x: centerX,
      y: centerY,
      text: '',
      author: userName || `User ${Math.floor(Math.random() * 1000)}`,
      timestamp: new Date().toISOString(),
      isNew: true
    });
  };

  const handleDeleteShape = (shapeId) => {
    if (!socket || !pdfId) return;
    
    setShapes(prev => prev.filter(s => s.id !== shapeId));
    socket.emit('delete-shape', {
      pdfId,
      shapeId
    });
    setSelectedShapeId(null);
  };

  const handleShapeColorChange = (shapeId, newColor) => {
    if (!socket || !pdfId) return;
    
    const updatedShapes = shapes.map(s => 
      s.id === shapeId ? { ...s, color: newColor } : s
    );
    setShapes(updatedShapes);
    
    socket.emit('update-shape', {
      pdfId,
      shapeId,
      updates: { color: newColor }
    });
  };

  const handleClearDrawing = () => {
    setDrawingShape(null);
    setIsDrawingMode(false);
    setSelectedShapeType(null);
    setSelectedShapeId(null);
    drawingStartRef.current = null;
    isDraggingRef.current = false;
  };

  // Comment delete/update handlers for CommentPanel
  const handleDeleteComment = (commentId) => {
    if (socket && pdfId) {
      socket.emit('delete-comment', { pdfId, commentId });
    }
  };

  const handleUpdateComment = (commentId, newText) => {
    if (socket && pdfId) {
      socket.emit('update-comment', {
        pdfId,
        commentId,
        updates: { text: newText }
      });
    }
  };

  const handleReplyComment = (commentId, reply) => {
    if (!socket || !pdfId) return;
      const comment = comments.find(c => c.id === commentId);
    const newReplies = [...(Array.isArray(comment?.replies) ? comment.replies : []), reply];
    // Optimistic update: show reply in UI immediately
    onCommentsChange(prev =>
      prev.map(c =>
        c.id === commentId ? { ...c, replies: newReplies } : c
      )
    );
      socket.emit('update-comment', {
        pdfId,
        commentId,
      updates: { replies: newReplies }
      });
  };

  const handleMarkCommentRead = (commentId, isRead = true) => {
    if (socket && pdfId) {
      socket.emit('update-comment', {
        pdfId,
        commentId,
        updates: { isRead: !!isRead }
      });
    }
  };

  // Expose handlers to parent via callback refs
  useEffect(() => {
    if (onCommentDelete && typeof onCommentDelete === 'object' && 'current' in onCommentDelete) {
      onCommentDelete.current = handleDeleteComment;
    }
    if (onCommentUpdate && typeof onCommentUpdate === 'object' && 'current' in onCommentUpdate) {
      onCommentUpdate.current = handleUpdateComment;
    }
    if (onCommentReply && typeof onCommentReply === 'object' && 'current' in onCommentReply) {
      onCommentReply.current = handleReplyComment;
    }
    if (onCommentMarkRead && typeof onCommentMarkRead === 'object' && 'current' in onCommentMarkRead) {
      onCommentMarkRead.current = handleMarkCommentRead;
    }
    if (exportRef && typeof exportRef === 'object' && 'current' in exportRef) {
      exportRef.current = { handleSavePDF, setPageNumber, setSelectedCommentId: onSelectComment ? (c) => onSelectComment(c ? { id: c } : null) : undefined };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, pdfId, comments]);

  const handleToggleDrawingMode = () => {
    if (isDrawingMode) {
      handleClearDrawing();
    } else {
      setIsDrawingMode(true);
      setSelectedShapeType(SHAPE_TYPES.RECTANGLE);
      setIsAddingComment(false);
      setSelectedShapeId(null);
    }
  };

  const handleExport = async (exportType) => {
    // exportType: 'pdf', 'csv', or 'both'
    if (!pdfId) {
      alert('No PDF loaded');
      return;
    }

    setShowExportDialog(false);

    try {
      const apiUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      
      // Download PDF if requested
      if (exportType === 'pdf' || exportType === 'both') {
        try {
          const pdfResponse = await fetch(`${apiUrl}/api/pdf/${pdfId}/download`);
        
          if (!pdfResponse.ok) {
            const errorData = await pdfResponse.json().catch(() => ({ error: 'Unknown error' }));
            const errorMessage = errorData.error || `Server returned ${pdfResponse.status}: ${pdfResponse.statusText}`;
            console.error('PDF download failed:', errorMessage, 'pdfId:', pdfId);
            throw new Error(errorMessage);
          }

          // Check if response is actually a PDF
          const contentType = pdfResponse.headers.get('Content-Type');
          if (contentType && !contentType.includes('application/pdf')) {
            const errorText = await pdfResponse.text();
            console.error('Server returned non-PDF:', errorText);
            throw new Error('Server did not return a valid PDF');
          }

          // Get the PDF filename from Content-Disposition header or use default
          const pdfContentDisposition = pdfResponse.headers.get('Content-Disposition');
          let pdfFilename = 'document_with_comments.pdf';
          if (pdfContentDisposition) {
            const filenameMatch = pdfContentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              pdfFilename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
            }
          }

          // Create blob and download PDF
          const pdfBlob = await pdfResponse.blob();
          
          // Verify blob is not empty and is a PDF
          if (pdfBlob.size === 0) {
            throw new Error('Received empty PDF file');
          }
          
          // Check if blob starts with PDF magic bytes
          const blobStart = await pdfBlob.slice(0, 4).text();
          if (!blobStart.startsWith('%PDF')) {
            console.error('Blob does not appear to be a PDF. First bytes:', blobStart);
            throw new Error('Downloaded file is not a valid PDF');
          }
          
          // Download PDF
          const pdfUrl = window.URL.createObjectURL(pdfBlob);
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.download = pdfFilename;
          document.body.appendChild(pdfLink);
          pdfLink.click();
          window.URL.revokeObjectURL(pdfUrl);
          document.body.removeChild(pdfLink);
        } catch (pdfError) {
          console.error('Error downloading PDF:', pdfError);
          if (exportType === 'pdf') {
            // If session is lost but we have the original PDF URL, offer to download that
            if (pdfError.message.includes('session not found') || pdfError.message.includes('PDF not found')) {
              const downloadOriginal = window.confirm(
                'PDF session expired. Would you like to download the original PDF file instead?'
              );
              if (downloadOriginal && pdfUrl) {
                try {
                  const originalResponse = await fetch(pdfUrl);
                  if (originalResponse.ok) {
                    const blob = await originalResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = pdfUrl.split('/').pop() || 'document.pdf';
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                    return;
                  }
                } catch (fallbackError) {
                  console.error('Failed to download original PDF:', fallbackError);
                }
              }
            }
            alert(`Failed to download PDF: ${pdfError.message}`);
          } else {
            console.warn('PDF download failed, but continuing with CSV...');
          }
        }
      }

      // Download CSV file if requested
      if (exportType === 'csv' || exportType === 'both') {
        if (comments && comments.length > 0) {
          try {
            const csvResponse = await fetch(`${apiUrl}/api/pdf/${pdfId}/download-csv`);
            
            if (!csvResponse.ok) {
              console.warn('Failed to download CSV:', csvResponse.statusText);
              if (exportType === 'csv') {
                throw new Error('Failed to download CSV');
              }
            } else {
              const csvContentDisposition = csvResponse.headers.get('Content-Disposition');
              let csvFilename = 'document_comments.csv';
              if (csvContentDisposition) {
                const filenameMatch = csvContentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                  csvFilename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
                }
              }

              const csvBlob = await csvResponse.blob();
              const csvUrl = window.URL.createObjectURL(csvBlob);
              const csvLink = document.createElement('a');
              csvLink.href = csvUrl;
              csvLink.download = csvFilename;
              document.body.appendChild(csvLink);
              // Small delay to ensure PDF download starts first if both
              setTimeout(() => {
                csvLink.click();
                window.URL.revokeObjectURL(csvUrl);
                document.body.removeChild(csvLink);
              }, exportType === 'both' ? 100 : 0);
            }
          } catch (csvError) {
            console.warn('Error downloading CSV:', csvError);
            if (exportType === 'csv') {
              alert(`Failed to download CSV: ${csvError.message}`);
            }
          }
        } else if (exportType === 'csv') {
          alert('No comments available to export as CSV');
        }
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert(`Failed to export: ${error.message}`);
    }
  };

  const handleSavePDF = () => {
    if (!pdfId) {
      alert('No PDF loaded');
      return;
    }

    if ((!comments || comments.length === 0) && (!shapes || shapes.length === 0) && (!highlights || highlights.length === 0)) {
      const confirmSave = window.confirm('No comments, shapes, or highlights to save. Do you want to download the original PDF?');
      if (!confirmSave) return;
      handleExport('pdf');
      return;
    }

    setShowExportDialog(true);
  };

  // Button availability: only one mode (comment / draw / highlight) should be active at a time.
  // When one is active, the others are visually disabled so the user can't start them
  // until the current mode is cancelled.
  const isAddCommentDisabled = isDrawingMode || isHighlightMode;
  const isDrawShapesDisabled = isAddingComment || isHighlightMode;
  const isHighlightDisabled = isAddingComment || isDrawingMode;

  return (
    <div className="pdf-viewer-container" ref={containerRef}>
      {documentReady && isDrawingMode && (
        <ShapeToolbar
          selectedShape={selectedShapeType}
          onShapeSelect={handleShapeTypeSelect}
          selectedColor={selectedColor}
          onColorSelect={setSelectedColor}
          isDrawing={!!selectedShapeType}
          onClear={handleClearDrawing}
        />
      )}
      {documentReady && isHighlightMode && (
        <HighlightToolbar
          selectedColor={selectedHighlightColor}
          onColorSelect={setSelectedHighlightColor}
          onCancel={() => {
            setIsHighlightMode(false);
            setSelectedShapeType(null);
            setIsAddingComment(false);
          }}
        />
      )}
      {documentReady && !isDrawingMode && !isHighlightMode && (
        <div className="pdf-controls">
          <div className="controls-left">
          <div className="active-users-container">
            <button
              className="active-users-trigger"
              onClick={() => setShowUsersDropdown(!showUsersDropdown)}
              type="button"
            >
              {/* Only show counter in header; actual user list is in dropdown */}
              <span className="users-count">{activeUsers.length + 1} online</span>
              <span className="dropdown-arrow">{showUsersDropdown ? '▲' : '▼'}</span>
            </button>
            {showUsersDropdown && (
              <div className="users-dropdown">
                <div className="users-dropdown-header">
                  <span className="users-dropdown-title">Active Users</span>
                  <span className="users-dropdown-count">{activeUsers.length + 1} online</span>
                </div>
                <div className="users-dropdown-list">
                  <div className="user-dropdown-item current-user">
                    <div 
                      className="user-dropdown-avatar"
                      style={{ backgroundColor: '#2f6dd3' }}
                    >
                      {(userName || 'You').charAt(0).toUpperCase()}
                    </div>
                    <div className="user-dropdown-info">
                      <span className="user-dropdown-name">{userName || 'You'}</span>
                      <span className="user-dropdown-label">(You)</span>
                    </div>
                  </div>
                  {activeUsers.map(user => (
                    <div key={user.id} className="user-dropdown-item">
                      <div 
                        className="user-dropdown-avatar"
                        style={{ backgroundColor: user.color }}
                      >
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-dropdown-info">
                        <span className="user-dropdown-name">{user.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setIsAddingComment(true);
              setSelectedShapeType(null);
              setSelectedShapeId(null);
              setIsDrawingMode(false);
              setIsHighlightMode(false);
            }}
            className={`add-comment-btn ${isAddingComment ? 'active' : ''}`}
            disabled={isAddCommentDisabled && !isAddingComment}
          >
            {isAddingComment ? 'Click on PDF to add comment' : ' Add Comment'}
          </button>
          <button
            onClick={handleToggleDrawingMode}
            className={`shape-mode-btn ${isDrawingMode ? 'active' : ''}`}
            disabled={isDrawShapesDisabled && !isDrawingMode}
            title="Draw shapes on PDF"
          >
            {isDrawingMode ? '✕ Cancel Drawing' : ' Draw Shapes'}
          </button>
          <button
            onClick={() => {
              setIsHighlightMode(!isHighlightMode);
              setIsDrawingMode(false);
              setIsAddingComment(false);
              setSelectedShapeType(null);
              setSelectedShapeId(null);
            }}
            className={`highlight-mode-btn ${isHighlightMode ? 'active' : ''}`}
            disabled={isHighlightDisabled && !isHighlightMode}
            title="Highlight text in PDF"
          >
            {isHighlightMode ? 'Cancel Highlight' : 'Highlight Text'}
          </button>
          {isAddingComment && (
            <button onClick={() => setIsAddingComment(false)} className="cancel-btn">
              Cancel
            </button>
          )}
          </div>
          <div className="controls-right">
            {sharePanel}
            <button 
              className="export-header-btn" 
              onClick={handleSavePDF}
              disabled={(!comments || comments.length === 0) && (!shapes || shapes.length === 0) && (!highlights || highlights.length === 0)}
              title="Export PDF with comments, shapes, and highlights"
            >
              Export
            </button>
          </div>
        </div>
      )}

      {documentReady && !isDrawingMode && !isHighlightMode && selectedShapeId && (
        <div className="shape-actions-dropdown">
            <div className="shape-actions">
              <span className="shape-edit-label">Editing Shape:</span>
            <button
              onClick={handleAddCommentToShape}
              className="add-comment-shape-btn"
              title="Add comment to this shape"
            >
              Add comment
            </button>
              <button
                onClick={() => handleDeleteShape(selectedShapeId)}
                className="delete-shape-btn"
                title="Delete shape"
              >
              Delete
              </button>
              <div className="color-picker-inline">
                <span className="color-label">Color:</span>
                {COLORS.map(color => (
                  <button
                    key={color.value}
                    className={`color-btn-small ${shapes.find(s => s.id === selectedShapeId)?.color === color.value ? 'active' : ''}`}
                    style={{ backgroundColor: color.value }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShapeColorChange(selectedShapeId, color.value);
                    }}
                    title={color.name}
                  />
                ))}
              </div>
              <button
                onClick={() => setSelectedShapeId(null)}
                className="close-edit-btn"
                title="Close editing"
              >
                ✕
              </button>
            </div>
        </div>
      )}

      <div className="pdf-document">
        {!pdfUrl ? (
          <div className="loading" style={{ 
            padding: '3rem', 
            textAlign: 'center', 
            background: 'white',
            color: '#666',
            fontSize: '1rem'
          }}>
            No PDF loaded. Please upload a PDF file.
          </div>
        ) : (
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => {
            console.error('PDF load error:', error);
            const errorMsg = error?.message || error?.toString() || '';
            let userMessage = 'Failed to load PDF. Please try uploading again.';
            if (errorMsg.includes('Failed to fetch') || errorMsg.includes('ERR_CONNECTION_REFUSED') || errorMsg.includes('NetworkError') || pdfUrl?.includes(config.API_URL)) {
              userMessage = `Cannot connect to server at ${config.API_URL}. Please make sure the server is running.`;
            }
            alert(userMessage);
          }}
          loading={null}
            error={
              <div className="error" style={{ 
                padding: '3rem', 
                textAlign: 'center', 
                background: 'white',
                color: '#dc3545',
                fontSize: '1.1rem'
              }}>
                <div>Failed to load PDF. Please try uploading again.</div>
                {pdfUrl?.includes(config.API_URL) && (
                  <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                    Make sure the server is running at {config.API_URL}
                  </div>
                )}
              </div>
            }
            options={pdfOptions}
            key={pdfUrl}
          >
            {numPages && numPages > 0 ? Array.from(new Array(numPages), (el, index) => {
            const pageNum = index + 1;
            const isPageVisible = pageNum <= maxRenderedPage;
            const pageComments = getCommentsForPage(pageNum);
        
            // If this page hasn't been scheduled for rendering yet, don't show anything for it.
            if (!isPageVisible) {
              return null;
            }
        
            return (
              <div
                key={`page_${pageNum}`}
                className="page-wrapper visible"
                ref={el => {
                  if (el) pageRefs.current[pageNum] = el;
                }}
                style={{
                  // Hide all pages visually until the first page has finished rendering
                  opacity: isFirstPageRendered ? 1 : 0,
                  pointerEvents: isFirstPageRendered ? 'auto' : 'none',
                  transition: 'opacity 0.2s ease-in-out'
                }}
              >
                <div
                  className="page-container"
                  onClick={(e) => {
                    // Only handle click for comments if not in highlight mode
                    if (isAddingComment && !isHighlightMode) {
                      handlePageClick(e, pageNum);
                    }
                  }}
                    onMouseDown={(e) => {
                    // Handle shape start if in drawing mode and not in highlight/comment mode
                    if (isDrawingMode && selectedShapeType && !isHighlightMode && !isAddingComment) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShapeStart(e, pageNum);
                    }
                  }}
                  onMouseMove={(e) => {
                    handleMouseMove(e);
                    // Handle shape move if in drawing mode
                    if (isDrawingMode && selectedShapeType && !isHighlightMode && !isAddingComment) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShapeMove(e, pageNum);
                    }
                  }}
                  onMouseUp={(e) => {
                    // Handle shape end if in drawing mode
                    if (isDrawingMode && selectedShapeType && !isHighlightMode && !isAddingComment) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShapeEnd();
                    }
                  }}
                  onMouseLeave={(e) => {
                    // Handle shape end if in drawing mode
                    if (isDrawingMode && selectedShapeType && !isHighlightMode && !isAddingComment) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShapeEnd();
                    }
                  }}
                  style={{ 
                    position: 'relative', 
                    cursor: (isDrawingMode && selectedShapeType) ? 'crosshair' : (isHighlightMode ? 'text' : 'default'),
                    userSelect: isHighlightMode ? 'text' : 'none',
                    WebkitUserSelect: isHighlightMode ? 'text' : 'none',
                    MozUserSelect: isHighlightMode ? 'text' : 'none',
                  msUserSelect: isHighlightMode ? 'text' : 'none'
                  }}
                >
                  {/* Render shapes on this page */}
                  {getShapesForPage(pageNum).map(shape => {
                    // For line and arrow, render using absolute coordinates so the start point stays fixed
                    if (shape.type === 'line' || shape.type === 'arrow') {
                      return (
                        <div
                          key={shape.id}
                          className={`shape-item ${selectedShapeId === shape.id ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShapeClick(e, shape.id);
                          }}
                          onMouseDown={(e) => {
                            if (!isDrawingMode || !selectedShapeType) {
                              e.stopPropagation();
                            }
                          }}
                          style={{
                            position: 'absolute',
                            left: '0%',
                            top: '0%',
                            width: '100%',
                            height: '100%',
                            cursor: (isDrawingMode && selectedShapeType) ? 'default' : 'pointer',
                        zIndex: selectedShapeId === shape.id ? 10 : 5,
                        // When highlighting text, let mouse events pass through shapes
                        pointerEvents: (isDrawingMode && selectedShapeType) || isHighlightMode ? 'none' : 'auto'
                          }}
                        >
                          <ShapeRenderer
                            shape={shape}
                            pageWidth={100}
                            pageHeight={100}
                          />
                          {selectedShapeId === shape.id && (
                            <div className="shape-selection-handle" />
                          )}
                        </div>
                      );
                    }

                    // Other shapes use a bounding box container
                    const left = Math.min(shape.x1, shape.x2);
                    const top = Math.min(shape.y1, shape.y2);
                    const width = Math.abs(shape.x2 - shape.x1);
                    const height = Math.abs(shape.y2 - shape.y1);
                    
                    return (
                      <div
                        key={shape.id}
                        className={`shape-item ${selectedShapeId === shape.id ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShapeClick(e, shape.id);
                        }}
                        onMouseDown={(e) => {
                          if (!isDrawingMode || !selectedShapeType) {
                            e.stopPropagation();
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          top: `${top}%`,
                          width: `${width}%`,
                          height: `${height}%`,
                          cursor: (isDrawingMode && selectedShapeType) ? 'default' : 'pointer',
                          zIndex: selectedShapeId === shape.id ? 10 : 5,
                          // When highlighting text, let mouse events pass through shapes
                          pointerEvents: (isDrawingMode && selectedShapeType) || isHighlightMode ? 'none' : 'auto'
                        }}
                      >
                        <ShapeRenderer
                          shape={shape}
                          pageWidth={100}
                          pageHeight={100}
                        />
                        {selectedShapeId === shape.id && (
                          <div className="shape-selection-handle" />
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Render currently drawing shape */}
                  {drawingShape && drawingShape.page === pageNum && (
                    drawingShape.type === 'line' || drawingShape.type === 'arrow' ? (
                      <div
                        style={{
                          position: 'absolute',
                          left: '0%',
                          top: '0%',
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          zIndex: 6
                        }}
                      >
                        <ShapeRenderer
                          shape={drawingShape}
                          pageWidth={100}
                          pageHeight={100}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${Math.min(drawingShape.x1, drawingShape.x2)}%`,
                          top: `${Math.min(drawingShape.y1, drawingShape.y2)}%`,
                          width: `${Math.abs(drawingShape.x2 - drawingShape.x1)}%`,
                          height: `${Math.abs(drawingShape.y2 - drawingShape.y1)}%`,
                          pointerEvents: 'none',
                          zIndex: 6
                        }}
                      >
                        <ShapeRenderer
                          shape={drawingShape}
                          pageWidth={100}
                          pageHeight={100}
                        />
                      </div>
                    )
                  )}

                  {/* Render other users' cursors on this page */}
                  {Object.entries(userCursors)
                    .filter(([_, cursor]) => cursor.page === pageNum)
                    .map(([userId, cursor]) => (
                      <div
                        key={userId}
                        className="user-cursor"
                        style={{
                          position: 'absolute',
                          left: `${cursor.position.x}%`,
                          top: `${cursor.position.y}%`,
                          transform: 'translate(-50%, -50%)',
                          '--cursor-color': cursor.color
                        }}
                        title={cursor.userName}
                      >
                        <div className="cursor-pointer" style={{ borderColor: cursor.color }}>
                          <div className="cursor-name" style={{ backgroundColor: cursor.color }}>
                            {cursor.userName}
                          </div>
                        </div>
                      </div>
                    ))}
                  <Page
                    key={`page_${pageNum}`}
                    pageNumber={pageNum}
                    scale={scale}
                    renderTextLayer={!isDrawingMode}
                    renderAnnotationLayer={false}
                    loading={null}
                    onLoadSuccess={() => {
                      // Page loaded successfully
                    }}
                    onRenderSuccess={() => {
                      // Mark when the first page finishes rendering to hide overlay
                      setIsFirstPageRendered(true);
                    }}
                    onLoadError={(error) => {
                      // Silently handle worker-related errors
                      const errorMsg = error?.message || error?.toString() || '';
                      if (!errorMsg.includes('sendWithPromise') && 
                          !errorMsg.includes('sendWithStream') &&
                          !errorMsg.includes('WorkerTransport') &&
                          !errorMsg.includes('Cannot read properties of null')) {
                        console.error('PDF page load error:', error);
                      }
                    }}
                    onRenderError={(error) => {
                      // Silently handle worker-related errors
                      const errorMsg = error?.message || error?.toString() || '';
                      if (!errorMsg.includes('sendWithPromise') && 
                          !errorMsg.includes('sendWithStream') &&
                          !errorMsg.includes('WorkerTransport') &&
                          !errorMsg.includes('Cannot read properties of null')) {
                        console.error('PDF page render error:', error);
                      }
                    }}
                    onGetTextError={(error) => {
                      // Silently handle worker-related errors
                      const errorMsg = error?.message || error?.toString() || '';
                      if (!errorMsg.includes('sendWithPromise') && 
                          !errorMsg.includes('sendWithStream') &&
                          !errorMsg.includes('WorkerTransport') &&
                          !errorMsg.includes('Cannot read properties of null')) {
                        console.error('PDF text extraction error:', error);
                      }
                    }}
                  />
                  
                  {/* Render text highlights on this page */}
                  {getHighlightsForPage(pageNum).map(highlight => (
                    <div
                      key={highlight.id}
                      className="text-highlight"
                      style={{
                        position: 'absolute',
                        left: `${highlight.x}%`,
                        top: `${highlight.y}%`,
                        width: `${highlight.width}%`,
                        height: `${highlight.height}%`,
                        backgroundColor: highlight.color || selectedHighlightColor,
                        opacity: 0.3,
                        pointerEvents: 'none',
                        zIndex: 4
                      }}
                      title={highlight.text || 'Highlighted text'}
                    />
                  ))}

                  {/* Render comments on this page */}
                  {pageComments.map(comment => (
                    <React.Fragment key={comment.id}>
                      <div
                        className="comment-marker"
                        style={{
                          position: 'absolute',
                          left: `${comment.x}%`,
                          top: `${comment.y}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: selectedCommentId === comment.id ? 20 : 10,
                          // When highlighting text, let mouse events pass through comment pins
                          pointerEvents: isHighlightMode ? 'none' : 'auto'
                        }}
                        title={comment.text || 'No comment text'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectComment?.(selectedCommentId === comment.id ? null : comment);
                        }}
                      >
                        💬
                      </div>
                      {selectedCommentId === comment.id && (() => {
                        const fullComment = comments.find((c) => c.id === comment.id) || comment;
                        if (fullComment.replies && fullComment.replies.length > 0) {
                          console.log('📋 CommentDisplay - Comment has replies:', {
                            commentId: fullComment.id,
                            repliesCount: fullComment.replies.length,
                            replies: fullComment.replies
                          });
                        }
                        return (
                        <CommentDisplay
                            comment={fullComment}
                          x={comment.x}
                          y={comment.y}
                          onClose={() => onSelectComment?.(null)}
                        />
                        );
                      })()}
                    </React.Fragment>
                  ))}

                  {/* Temporary comment being added */}
                  {tempComment && tempComment.page === pageNum && (
                    <CommentInput
                      x={tempComment.x}
                      y={tempComment.y}
                      onSubmit={handleCommentSubmit}
                      onCancel={handleCommentCancel}
                    />
                  )}
                </div>
              </div>
            );
          }) : null}
        </Document>
        )}

        {/* Compact loading indicator for remaining pages (shown below loaded pages) */}
        {documentReady && pdfUrl && numPages && maxRenderedPage < numPages && (
          <div
            className="loading"
            style={{
              padding: '0.75rem 1rem',
              marginTop: '0.5rem',
              marginBottom: '1rem',
              background: '#ffffff',
              color: '#4b5563',
              fontSize: '0.85rem',
              minHeight: 'auto',
              boxShadow: 'none',
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}
          >
            Loading more pages...
          </div>
        )}
      </div>

      {/* Zoom controls docked vertically at bottom-left of viewer (temporarily disabled) */}
      {/*
      <div className="zoom-controls zoom-controls-bottom-left">
        <button
          className="zoom-btn zoom-out-btn"
          onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))}
          title="Zoom out (Ctrl + -)"
          type="button"
        >
          −
        </button>
        <span className="zoom-level">{Math.round(scale * 100)}%</span>
        <button
          className="zoom-btn zoom-in-btn"
          onClick={() => setScale(prev => Math.min(3.0, prev + 0.1))}
          title="Zoom in (Ctrl + +)"
          type="button"
        >
          +
        </button>
        <button
          className="zoom-btn zoom-reset-btn"
          onClick={() => setScale(1.0)}
          title="Reset zoom (Ctrl + 0)"
          type="button"
        >
          Reset
        </button>
      </div>
      */}

      {/* Export Options Dialog */}
      {showExportDialog && (
        <div className="export-dialog-overlay" onClick={() => setShowExportDialog(false)}>
          <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Export Options</h3>
            <p>Choose what you want to export:</p>
            <div className="export-options">
              <button
                className="export-option-btn"
                onClick={() => handleExport('pdf')}
                title="Export PDF with comments, shapes, and highlights"
              >
                PDF Only
              </button>
              <button
                className="export-option-btn"
                onClick={() => handleExport('csv')}
                disabled={!comments || comments.length === 0}
                title="Export CSV with comment locations"
              >
                CSV Only
              </button>
              <button
                className="export-option-btn"
                onClick={() => handleExport('both')}
                disabled={!comments || comments.length === 0}
                title="Export both PDF and CSV"
              >
               Both (PDF + CSV)
              </button>
            </div>
            <button
              className="export-cancel-btn"
              onClick={() => setShowExportDialog(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CommentInput = ({ x, y, onSubmit, onCancel }) => {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (text.trim()) {
      onSubmit(text.trim());
      setText('');
    }
  };

  // Position the input box to the right of the marker
  // If there's not enough space on the right, position it to the left
  const getPosition = () => {
    // Default: position to the right (12% offset)
    let leftPos = x + 12;
    let transform = 'translateY(-50%)';
    let lineDirection = 'right';
    
    // If the input would go off the right edge, position to the left
    if (leftPos > 80) {
      leftPos = x - 12;
      transform = 'translate(-100%, -50%)';
      lineDirection = 'left';
    }
    
    return {
      left: `${leftPos}%`,
      top: `${y}%`,
      transform: transform,
      lineDirection: lineDirection
    };
  };

  const position = getPosition();

  return (
    <>
      {/* Comment marker dot at click location */}
      <div
        className="comment-marker-dot"
        style={{
          position: 'absolute',
          left: `${x}%`,
          top: `${y}%`,
          transform: 'translate(-50%, -50%)',
          zIndex: 15
        }}
      />
      {/* Connecting line from marker to input box */}
      <svg
        className="comment-connector-line"
        style={{
          position: 'absolute',
          left: position.lineDirection === 'right' ? `${x}%` : `${x - 12}%`,
          top: `${y}%`,
          width: '12%',
          height: '2px',
          zIndex: 14,
          pointerEvents: 'none',
          transform: 'translateY(-50%)'
        }}
      >
        <line
          x1={position.lineDirection === 'right' ? '0%' : '100%'}
          y1="0"
          x2={position.lineDirection === 'right' ? '100%' : '0%'}
          y2="0"
          stroke="#667eea"
          strokeWidth="2"
          strokeDasharray="4,4"
        />
      </svg>
      <div
        ref={containerRef}
        className="comment-input-container"
        style={{
          position: 'absolute',
          left: position.left,
          top: position.top,
          transform: position.transform,
          zIndex: 16
        }}
        onClick={(e) => e.stopPropagation()}
      >
      <form onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Enter your comment..."
          rows="3"
          className="comment-textarea"
        />
        <div className="comment-input-buttons">
          <button type="submit" className="submit-btn">Save</button>
          <button type="button" onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
        </div>
      </form>
    </div>
    </>
  );
};

const CommentDisplay = ({ comment, x, y, onClose }) => {
  const containerRef = useRef(null);

  // Position the display box to the right of the marker
  // If there's not enough space on the right, position it to the left
  const getPosition = () => {
    // Default: position to the right (12% offset)
    let leftPos = x + 12;
    let transform = 'translateY(-50%)';
    let lineDirection = 'right';
    
    // If the input would go off the right edge, position to the left
    if (leftPos > 80) {
      leftPos = x - 12;
      transform = 'translate(-100%, -50%)';
      lineDirection = 'left';
    }
    
    return {
      left: `${leftPos}%`,
      top: `${y}%`,
      transform: transform,
      lineDirection: lineDirection
    };
  };

  const position = getPosition();

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Ensure we have replies array (defensive check - handle various formats)
  let replies = [];
  if (comment?.replies) {
    if (Array.isArray(comment.replies)) {
      replies = comment.replies.filter((r) => r && typeof r === 'object' && (r.text != null || r.author != null));
    } else if (typeof comment.replies === 'object') {
      // Handle case where replies might be an object instead of array
      replies = Object.values(comment.replies).filter((r) => r && typeof r === 'object' && (r.text != null || r.author != null));
    }
  }
  
  if (replies.length > 0) {
    console.log('💬 CommentDisplay rendering with replies:', {
      commentId: comment?.id,
      repliesCount: replies.length,
      replies: replies,
      rawReplies: comment?.replies
    });
  } else if (comment) {
    console.log('⚠️ CommentDisplay - No replies found:', {
      commentId: comment.id,
      hasRepliesProp: 'replies' in comment,
      repliesValue: comment.replies,
      repliesType: typeof comment.replies
    });
  }

  return (
    <>
      {/* Connecting line from marker to display box */}
      <svg
        className="comment-connector-line"
        style={{
          position: 'absolute',
          left: position.lineDirection === 'right' ? `${x}%` : `${x - 12}%`,
          top: `${y}%`,
          width: '12%',
          height: '2px',
          zIndex: 14,
          pointerEvents: 'none',
          transform: 'translateY(-50%)'
        }}
      >
        <line
          x1={position.lineDirection === 'right' ? '0%' : '100%'}
          y1="0"
          x2={position.lineDirection === 'right' ? '100%' : '0%'}
          y2="0"
          stroke="#667eea"
          strokeWidth="2"
          strokeDasharray="4,4"
        />
      </svg>
      <div
        ref={containerRef}
        className="comment-display-container"
        style={{
          position: 'absolute',
          left: position.left,
          top: position.top,
          transform: position.transform,
          zIndex: 16
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="comment-display-header">
          <div className="comment-display-author">
            <strong>{comment.author || 'Unknown'}</strong>
            {comment.isFromPDF && (
              <span className="pdf-annotation-badge" title="Extracted from PDF">
                📄 PDF
              </span>
            )}
          </div>
          <button
            className="comment-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close comment"
          >
            ✕
          </button>
        </div>
        <div className="comment-display-meta">
          Page {comment.page} • {formatTime(comment.timestamp)}
        </div>
        <div className="comment-display-text">
          {comment.text || <em className="empty-comment">No text</em>}
        </div>
        {replies.length > 0 && (
          <div className="comment-display-replies">
            <div className="comment-display-replies-label">Replies</div>
            {replies.map((r, idx) => (
              <div key={r.id || idx} className="comment-display-reply">
                <span className="comment-display-reply-author">{r.author || 'Unknown'}:</span>
                <span className="comment-display-reply-text">{r.text || ''}</span>
                {r.timestamp && (
                  <span className="comment-display-reply-time">{formatTime(r.timestamp)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default PDFViewer;

